//== pure calculation engine for the FHS redemption calculator
//== no DOM dependencies; importable from a Node test script

export const PRODUCTS = Object.freeze({
  NEW_BUILD: 'newBuild',
  THP: 'thp',
  SELF_BUILD: 'selfBuild',
})

const DEFAULT_MIN_PARTIAL_PCT = 0.05

//== converts the user-facing percentage form (e.g. 5 for 5%) to a decimal (0.05)
const pctToDecimal = (pct) => pct / 100

const isPositiveFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0
const isNonNegativeFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0

//== the original FHS equity share as a decimal (e.g. 0.30), derived from the
//== property-details inputs alone so callers can compute it before the full
//== input set that calculate() needs is present. SB denominator is buildCost
//== alone (matches the self-build spreadsheet cell D9: =D7/D6); NB / THP use
//== purchasePrice. returns null when the inputs are missing or non-positive.
//== single source of truth shared by render.js (derived display) and
//== validate.js (the step-1 over-max / over-original hard blocks).
export function originalEquitySharePct({ product, inputs } = {}) {
  const oe = inputs?.originalEquity
  if (!Number.isFinite(oe) || oe < 0) return null
  const denom = product === PRODUCTS.SELF_BUILD ? inputs?.buildCost : inputs?.purchasePrice
  if (!Number.isFinite(denom) || denom <= 0) return null
  return oe / denom
}

//== self-build only: while the build is incomplete no partial redemption is
//== permitted, so no prior redemption can exist and the customer's current
//== equity share must equal the original. gated on build completeness alone,
//== not on the early-redemption window - the rule follows from partials being
//== unavailable, not from the drawdown date, so it holds outside the window
//== too (client 2026-07-21, their scenarios 5 - 8). buildComplete is compared
//== strictly to false: it seeds to true from the default-checked radio, and an
//== unset value should not lock the field.
export function isCurrentEquityLocked(state) {
  return state?.product === PRODUCTS.SELF_BUILD && state?.buildComplete === false
}

//== the current equity share to calculate with, in percentage form. normally
//== whatever the customer entered; when locked it is derived from the original
//== equity at full precision (14.2857…% rather than a typed 14.29%), so a full
//== redemption in that state returns exactly the amount drawn down instead of
//== a figure a cent or two out.
export function resolveCurrentEquityPct(state) {
  if (!isCurrentEquityLocked(state)) return state?.inputs?.currentEquityPct ?? null
  const share = originalEquitySharePct(state)
  return Number.isFinite(share) ? share * 100 : null
}

//== state.inputs with the locked current share substituted in. every call site
//== that feeds calculate() goes through this rather than using state.inputs
//== directly, so the lock is applied at the point of use and no derived value
//== is ever written back into state.
export function resolveInputs(state) {
  const inputs = state?.inputs ?? {}
  if (!isCurrentEquityLocked(state)) return inputs
  return { ...inputs, currentEquityPct: resolveCurrentEquityPct(state) }
}

export function calculate({ product, inputs, redemption, scheme, early = false } = {}) {
  if (!product || !inputs) return null
  const minPartialPct = scheme?.minPartialPct ?? DEFAULT_MIN_PARTIAL_PCT

  //== inside the early-redemption window no new valuation is required, so the
  //== figure is based on the original drawdown value (purchase price / build
  //== cost) rather than a current valuation. one branch covers all products.
  if (early) {
    return calculateEarly({ product, inputs, redemption, minPartialPct })
  }
  if (product === PRODUCTS.SELF_BUILD) {
    return calculateSelfBuild({ inputs, redemption, minPartialPct })
  }
  if (product === PRODUCTS.NEW_BUILD || product === PRODUCTS.THP) {
    return calculateStandard({ inputs, redemption, minPartialPct })
  }
  return null
}

//== early-redemption branch: no new valuation, so the base is the original
//== drawdown value - purchase price (NB / THP) or build cost (SB, build cost
//== only per the client's self-build basis; no site discount). the redemption
//== amount is the customer's *current* remaining FHS equity share of that base,
//== so a prior redemption (a current share below the original) is reflected
//== automatically. when no prior redemption has been made the current share
//== equals the original share and the figure collapses to originalEquity.
function calculateEarly({ product, inputs, redemption, minPartialPct }) {
  const {
    purchasePrice,
    buildCost,
    originalEquity,
    currentEquityPct,
    serviceCharges = 0,
  } = inputs

  const base = product === PRODUCTS.SELF_BUILD ? buildCost : purchasePrice
  if (!isPositiveFinite(base)) return null
  if (!isNonNegativeFinite(originalEquity)) return null
  if (!isNonNegativeFinite(currentEquityPct)) return null

  const equityDecimal = pctToDecimal(currentEquityPct)
  const originalEquityPct = originalEquity / base
  const currentEquityValue = base * equityDecimal
  const minPartial = originalEquity * minPartialPct
  const maxPartial = currentEquityValue
  const fullRedemptionTotal = currentEquityValue + serviceCharges

  const redemptionFields = computeRedemptionFields({
    redemption,
    valuationBase: base,
    equityDecimal,
  })

  return {
    originalEquityPct,
    currentEquityValue,
    fullRedemptionTotal,
    minPartial,
    maxPartial,
    ...redemptionFields,
  }
}

function calculateStandard({ inputs, redemption, minPartialPct }) {
  const {
    purchasePrice,
    originalEquity,
    currentEquityPct,
    currentValuation,
    serviceCharges = 0,
  } = inputs

  if (!isPositiveFinite(purchasePrice)) return null
  if (!isPositiveFinite(currentValuation)) return null
  if (!isNonNegativeFinite(originalEquity)) return null
  if (!isNonNegativeFinite(currentEquityPct)) return null

  const equityDecimal = pctToDecimal(currentEquityPct)
  const originalEquityPct = originalEquity / purchasePrice
  const currentEquityValue = currentValuation * equityDecimal
  const minPartial = originalEquity * minPartialPct
  const maxPartial = currentEquityValue
  const fullRedemptionTotal = currentEquityValue + serviceCharges

  const redemptionFields = computeRedemptionFields({
    redemption,
    valuationBase: currentValuation,
    equityDecimal,
  })

  return {
    originalEquityPct,
    currentEquityValue,
    fullRedemptionTotal,
    minPartial,
    maxPartial,
    ...redemptionFields,
  }
}

function calculateSelfBuild({ inputs, redemption, minPartialPct }) {
  const {
    buildCost,
    siteValue,
    originalEquity,
    currentEquityPct,
    currentValuation,
    serviceCharges = 0,
  } = inputs

  if (!isPositiveFinite(buildCost)) return null
  if (!isNonNegativeFinite(siteValue)) return null
  if (!isPositiveFinite(currentValuation)) return null
  if (!isNonNegativeFinite(originalEquity)) return null
  if (!isNonNegativeFinite(currentEquityPct)) return null

  const equityDecimal = pctToDecimal(currentEquityPct)
  const totalCost = buildCost + siteValue
  //== self-build originalEquityPct denominator is buildCost alone (excludes
  //== siteValue), matching the client's self-build spreadsheet cell D9
  //== (=D7/D6). this diverges from the FHS public methodology page's worked
  //== examples and the React prototype, both of which use the total
  //== drawdown cost (buildCost + siteValue); the client chose the xls as
  //== the source of truth for this figure on 2026-05-13.
  const originalEquityPct = buildCost > 0 ? originalEquity / buildCost : 0
  const siteValuePct = totalCost > 0 ? siteValue / totalCost : 0
  const discountedValuation = currentValuation * (1 - siteValuePct)
  const currentEquityValue = discountedValuation * equityDecimal
  const minPartial = originalEquity * minPartialPct
  const maxPartial = currentEquityValue
  const fullRedemptionTotal = currentEquityValue + serviceCharges

  const redemptionFields = computeRedemptionFields({
    redemption,
    valuationBase: discountedValuation,
    equityDecimal,
  })

  return {
    originalEquityPct,
    siteValuePct,
    discountedValuation,
    currentEquityValue,
    fullRedemptionTotal,
    minPartial,
    maxPartial,
    ...redemptionFields,
  }
}

function computeRedemptionFields({ redemption, valuationBase, equityDecimal }) {
  if (!redemption || !Number.isFinite(redemption.amount)) return {}
  if (!Number.isFinite(valuationBase) || valuationBase <= 0) return {}

  const redemptionPct = redemption.amount / valuationBase
  const newEquityPct = equityDecimal - redemptionPct
  const newEquityValue = valuationBase * newEquityPct
  return { redemptionPct, newEquityPct, newEquityValue }
}
